import { FC, useContext, useRef } from "react";
import { ReportCustomizationContext } from "../ReportCustomizationContext";
import { handleToastError, handleToastSuccess, reportTypeToColumnsEndpointMap } from "../helpers/helpers";
import ExportService from "@app/exports/exportService";
import { ExportZipAnalysisRequestModel } from "@app/exports/exportModels";
import { NetworkAssessmentAnalysisType, TimeFrequency, ChangedGapsEnum } from "@app/projects/enumerators";
import { GenerateSection } from "../components/GenerateSection";
import { ColumnConfigurationSection } from "../components/ColumnConfigurationSection";
import { ServiceAreaSection } from "../components/ServiceAreaSection";
import { SpecialtySection } from "../components/SpecialtySection";
import { ReportCustomizationReducerActions } from "../interfaces/reportCustomizationReducerActionsEnum";
import { ReportType } from "../interfaces/reportTypeEnum";
import { ZipAnalysisStateCountyPairParameters } from "../interfaces/zipAnalysisStateCountyPairParameters";

export const SpecialtySummaryByZipCodeReport: FC = () => {
  const rcContext = useContext(ReportCustomizationContext);
  const exportService = useRef(new ExportService()).current;

  const getExportModel = () => {
    const { primaryId, secondaryId } = rcContext.getSelectedRunConfigurationIdsInChronologicalOrder();

    const zipSearchParams = { ...rcContext.routeState.zipProjectSearchParameters };
    const allZipPrimaryCounties: { [ssa: string]: { countyName: string, stateAbbreviation: string } } = {};
    rcContext.state.allServiceAreaCounties.forEach((s) => s.children.forEach((c) => allZipPrimaryCounties[c.countySSA] = ({ countyName: c.name, stateAbbreviation: s.name })));
    const stateCountyPairs: ZipAnalysisStateCountyPairParameters[] = [];
    rcContext.state.selectedCounties.forEach((c) => {
      stateCountyPairs.push(allZipPrimaryCounties[c]);
    });

    zipSearchParams.zipClass = rcContext.state.selectedZipClass === "all" ? null : rcContext.state.selectedZipClass;
    const changedGapsFilter = rcContext.state.changedGapsType === "" ? ChangedGapsEnum.Unknown : rcContext.state.changedGapsType;
    const servicingProviderModel: ExportZipAnalysisRequestModel = {
      changedGapsFilter,
      comparedProjectRunConfigurationId: secondaryId,
      customProjectId: parseInt(rcContext.routeParams.projectId),
      eMailOnSuccess: rcContext.state.shouldEmailLink,
      excludeZeroMembership: rcContext.state.excludeZeroMembership,
      exportName: rcContext.state.filename,
      formatType: rcContext.state.fileFormat,
      includeExpandableSpecialties: rcContext.state.includeExpandableSpecialties,
      includeFacilitySpecialties: rcContext.state.includeFacilitySpecialties,
      includePhysicianSpecialties: rcContext.state.includePhysicianSpecialties,
      networkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
      projectRunConfigurationId: primaryId,
      regionSetGapFilter: rcContext.state.serviceAreaGapsType,
      regionSetIds: changedGapsFilter === ChangedGapsEnum.Unknown ? rcContext.state.selectedCounties.map((sa) => parseInt(sa)) : [],
      reportCustomizationId: rcContext.state.selectedPreset.id,
      runAsynchronous: true,
      runFrequency: TimeFrequency.All, // as of right now, ZIP Projects doesn't support a monthly frequency
      searchParametersModel: zipSearchParams,
      sharedUsers: rcContext.state.sharedUsers,
      specialtyGapFilter: rcContext.state.specialtyGapsType,
      specialtyGroupCodes: rcContext.state.selectedSpecialties.map((sp) => sp.key),
      stateCountyPairsParametersModel: stateCountyPairs,
    };

    return servicingProviderModel;
  };

  // event handlers
  const handleGenerateClick = () => {
    rcContext.dispatch({ payload: { isGenerating: true }, type: ReportCustomizationReducerActions.ChangeIsGenerating });

    exportService.exportZipAnalysisResults(getExportModel())
      .then(() => handleToastSuccess(rcContext.state.filename),
        () => handleToastError(rcContext.state.filename)
      ).then(() => {
        rcContext.dispatch({ payload: { isGenerating: false }, type: ReportCustomizationReducerActions.ChangeIsGenerating });
      });
  }

  return (
    <>
      <div className="container ms-0">
        <ServiceAreaSection />

        <SpecialtySection />

        <ColumnConfigurationSection
          columnsEndpoint={reportTypeToColumnsEndpointMap.get(ReportType.ZipAnalysisResultsReport)}
          exportModel={getExportModel()}
          presets={rcContext.state.presets}
          selectedPreset={rcContext.state.selectedPreset}
          reportType={rcContext.getReportType()}
          onPresetChange={rcContext.changeSelectedPreset}
          onPresetsRefresh={rcContext.changeLoadedPresets}
        />
      </div>
      <GenerateSection
        settingsAreValid={rcContext.validateSettings()}
        onGenerate={handleGenerateClick}
      />
    </>
  );
}